import { c as _c } from "react/compiler-runtime";
// Модерация отзывов (fd-admin-05): очередь жалоб продавцов на отзывы о своих
// товарах. Сотрудник открывает отзыв вместе с жалобой и решает: «Скрыть
// отзыв» (не виден на витрине, не входит в рейтинг) или «Оставить» — с
// причиной, которую увидит продавец. Скрытый отзыв можно вернуть. Каждое
// решение пишется в журнал действий сотрудников (раздел «Журнал»).
import type { AdminReviewComplaint, ReviewComplaintStatus } from "@osmix/contracts";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Check, EyeOff, RotateCcw, ScrollText, Star } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { cn, formatDateTime } from "@/lib/utils";
import { useAuth } from "@/store";
import { reviewModerationAvailable, useReviewComplaint, useReviewComplaints, useReviewModeration } from "./review-complaints-api";
const PAGE_SIZE = 20;
const STATUSES: ReviewComplaintStatus[] = ["open", "accepted", "declined"];
const STATUS_CLASS: Record<ReviewComplaintStatus, string> = {
  open: "bg-amber-100 text-amber-800",
  accepted: "bg-red-100 text-red-800",
  declined: "bg-emerald-100 text-emerald-800"
};
const isStatus = (value: string | null): value is ReviewComplaintStatus => value === "open" || value === "accepted" || value === "declined";
function Stars(t0) {
  const $ = _c(6);
  const {
    value
  } = t0;
  const t1 = `${value} / 5`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [1, 2, 3, 4, 5];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== value) {
    t3 = t2.map(i => <Star key={i} className={cn("size-3.5", i <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30")} />);
    $[1] = value;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t3) {
    t4 = <span className="inline-flex items-center gap-0.5" role="img" aria-label={t1}>{t3}</span>;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function ProductCell(t0) {
  const $ = _c(12);
  const {
    product
  } = t0;
  let t1;
  if ($[0] !== product.photo) {
    t1 = product.photo ? <img src={product.photo} alt="" className="size-9 shrink-0 rounded object-cover" /> : <span className="size-9 shrink-0 rounded bg-muted" />;
    $[0] = product.photo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = product.name || product.id;
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="block truncate">{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== product.article) {
    t4 = product.article && <span className="block truncate text-xs text-muted-foreground">{product.article}</span>;
    $[4] = product.article;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <span className="min-w-0">{t3}{t4}</span>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t5) {
    t6 = <span className="flex min-w-0 items-center gap-2">{t1}{t5}</span>;
    $[9] = t1;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
export function ReviewComplaintsPage() {
  const $ = _c(86);
  const {
    t
  } = useTranslation();
  const [params, setParams] = useSearchParams();
  let t0;
  if ($[0] !== params) {
    t0 = isStatus(params.get("status")) ? params.get("status") as ReviewComplaintStatus : "open";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const status = t0;
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  let t1;
  if ($[2] !== params) {
    t1 = params.get("id");
    $[2] = params;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [openId, setOpenId] = useState(t1);
  let t2;
  if ($[4] !== q) {
    t2 = q.trim();
    $[4] = q;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== page || $[7] !== status || $[8] !== t2) {
    t3 = {
      status,
      q: t2,
      page,
      pageSize: PAGE_SIZE
    };
    $[6] = page;
    $[7] = status;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    data,
    isLoading,
    error
  } = useReviewComplaints(t3);
  let t4;
  if ($[10] !== params || $[11] !== setParams) {
    t4 = next => {
      const search = new URLSearchParams(params);
      if (next === "open") {
        search.delete("status");
      } else {
        search.set("status", next);
      }
      setParams(search, {
        replace: true
      });
      setPage(1);
    };
    $[10] = params;
    $[11] = setParams;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const setStatus = t4;
  let t5;
  if ($[13] !== data?.items) {
    t5 = data?.items ?? [];
    $[13] = data?.items;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const items = t5;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  let t6;
  if ($[15] !== t) {
    t6 = t("reviewComplaints.title");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6) {
    t7 = <h1 className="text-xl font-semibold">{t6}</h1>;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = t("reviewComplaints.subtitle");
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <p className="text-sm text-muted-foreground">{t8}</p>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t7 || $[24] !== t9) {
    t10 = <div>{t7}{t9}</div>;
    $[23] = t7;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t) {
    t11 = !reviewModerationAvailable && <p className="rounded-lg border p-4 text-sm text-muted-foreground">{t("reviewComplaints.unavailable")}</p>;
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== data || $[29] !== setStatus || $[30] !== status || $[31] !== t) {
    t12 = STATUSES.map(key => <button key={key} type="button" onClick={() => setStatus(key)} data-testid={`complaints-tab-${key}`} aria-pressed={status === key} className={cn("rounded-full border px-3 py-1 text-sm", status === key ? "border-primary bg-primary/5 font-medium" : "text-muted-foreground hover:bg-muted")}>{t(`reviewComplaints.tabs.${key}`)}{data ? ` (${data.counts[key]})` : ""}</button>);
    $[28] = data;
    $[29] = setStatus;
    $[30] = status;
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e => {
      setQ(e.target.value);
      setPage(1);
    };
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t) {
    t14 = t("reviewComplaints.searchPlaceholder");
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("reviewComplaints.search");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== q || $[39] !== t14 || $[40] !== t15) {
    t16 = <Input value={q} onChange={t13} placeholder={t14} aria-label={t15} className="w-full sm:ml-auto sm:w-72" />;
    $[38] = q;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t12 || $[43] !== t16) {
    t17 = <div className="flex flex-wrap items-center gap-2">{t12}{t16}</div>;
    $[42] = t12;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== error) {
    t18 = error && <p className="text-sm text-destructive">{(error as Error).message}</p>;
    $[45] = error;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== isLoading || $[48] !== t) {
    t19 = isLoading && <p className="text-sm text-muted-foreground">{t("reviewComplaints.loading")}</p>;
    $[47] = isLoading;
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== data || $[51] !== isLoading || $[52] !== items.length || $[53] !== status || $[54] !== t) {
    t20 = !isLoading && data && items.length === 0 && <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">{t(`reviewComplaints.empty.${status}`)}</div>;
    $[50] = data;
    $[51] = isLoading;
    $[52] = items.length;
    $[53] = status;
    $[54] = t;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== items || $[57] !== t) {
    t21 = items.length > 0 && <div className="flex flex-col gap-2 md:hidden">{items.map(item => <button key={item.id} type="button" data-testid={`complaint-card-${item.id}`} onClick={() => setOpenId(item.id)} className="flex min-w-0 flex-col gap-2 rounded-lg border p-3 text-left text-sm hover:bg-muted/40"><span className="flex items-center justify-between gap-2"><span className="text-xs text-muted-foreground">{formatDateTime(item.createdAt)}</span><span className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_CLASS[item.status])}>{t(`reviewComplaints.status.${item.status}`)}</span></span><ProductCell product={item.product} /><span className="flex items-center gap-2"><Stars value={item.review.rating} /><span className="truncate text-xs text-muted-foreground">{item.shop.name}</span></span><span className="line-clamp-2 break-words">{item.review.text || t("reviewComplaints.noText")}</span><span className="font-medium">{item.reason}</span></button>)}</div>;
    $[56] = items;
    $[57] = t;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== items || $[60] !== t) {
    t22 = items.length > 0 && <div className="hidden overflow-x-auto rounded-lg border md:block"><table className="w-full min-w-[860px] text-sm"><thead className="bg-muted/50 text-left text-xs text-muted-foreground"><tr><th className="p-2 font-medium">{t("reviewComplaints.columns.date")}</th><th className="p-2 font-medium">{t("reviewComplaints.columns.shop")}</th><th className="p-2 font-medium">{t("reviewComplaints.columns.product")}</th><th className="p-2 font-medium">{t("reviewComplaints.columns.rating")}</th><th className="p-2 font-medium">{t("reviewComplaints.columns.review")}</th><th className="p-2 font-medium">{t("reviewComplaints.columns.reason")}</th><th className="p-2 font-medium">{t("reviewComplaints.columns.status")}</th></tr></thead><tbody>{items.map(item_0 => <tr key={item_0.id} data-testid={`complaint-row-${item_0.id}`} onClick={() => setOpenId(item_0.id)} className="cursor-pointer border-t align-top hover:bg-muted/40"><td className="whitespace-nowrap p-2 text-muted-foreground">{formatDateTime(item_0.createdAt)}</td><td className="max-w-[160px] truncate p-2">{item_0.shop.name}</td><td className="max-w-[220px] p-2"><ProductCell product={item_0.product} /></td><td className="p-2"><Stars value={item_0.review.rating} /></td><td className="max-w-[260px] p-2"><span className="line-clamp-2 break-words">{item_0.review.text || t("reviewComplaints.noText")}</span></td><td className="max-w-[200px] p-2"><button type="button" className="text-left font-medium underline-offset-4 hover:underline" onClick={e_0 => {
                e_0.stopPropagation();
                setOpenId(item_0.id);
              }}>{item_0.reason}</button></td><td className="whitespace-nowrap p-2"><span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_CLASS[item_0.status])}>{t(`reviewComplaints.status.${item_0.status}`)}</span></td></tr>)}</tbody></table></div>;
    $[59] = items;
    $[60] = t;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== page || $[63] !== pages || $[64] !== t) {
    t23 = pages > 1 && <div className="flex items-center justify-end gap-2 text-sm"><Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(_temp)}>{t("reviewComplaints.prev")}</Button><span className="text-muted-foreground">{page} / {pages}</span><Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(_temp2)}>{t("reviewComplaints.next")}</Button></div>;
    $[62] = page;
    $[63] = pages;
    $[64] = t;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== items || $[67] !== openId) {
    let t25;
    if ($[69] !== openId) {
      t25 = item_1 => item_1.id === openId;
      $[69] = openId;
      $[70] = t25;
    } else {
      t25 = $[70];
    }
    t24 = items.find(t25);
    $[66] = items;
    $[67] = openId;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  let t25;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setOpenId(null);
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== openId || $[73] !== t24) {
    t26 = <ComplaintSheet id={openId} fallback={t24} onClose={t25} />;
    $[72] = openId;
    $[73] = t24;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== t10 || $[76] !== t11 || $[77] !== t17 || $[78] !== t18 || $[79] !== t19 || $[80] !== t20 || $[81] !== t21 || $[82] !== t22 || $[83] !== t23 || $[84] !== t26) {
    t27 = <div className="flex min-w-0 flex-col gap-4" data-testid="review-complaints">{t10}{t11}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t26}</div>;
    $[75] = t10;
    $[76] = t11;
    $[77] = t17;
    $[78] = t18;
    $[79] = t19;
    $[80] = t20;
    $[81] = t21;
    $[82] = t22;
    $[83] = t23;
    $[84] = t26;
    $[85] = t27;
  } else {
    t27 = $[85];
  }
  return t27;
}
function _temp2(p_0) {
  return p_0 + 1;
}
function _temp(p) {
  return p - 1;
}
type Pending = {
  kind: "hide" | "keep" | "restore";
} | null;
function ComplaintSheet(t0) {
  const $ = _c(38);
  const {
    id,
    fallback,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const user = useAuth(_temp3);
  let t1;
  if ($[0] !== user) {
    t1 = hasAdminPermission(user, "action.products.moderate");
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canModerate = t1;
  let t2;
  if ($[2] !== user) {
    t2 = hasAdminPermission(user, "action.audit.view");
    $[2] = user;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const canAudit = t2;
  const {
    data,
    error
  } = useReviewComplaint(id);
  const complaint = data ?? fallback;
  const [pending, setPending] = useState(null);
  const t3 = Boolean(id);
  let t4;
  if ($[4] !== onClose) {
    t4 = open => !open && onClose();
    $[4] = onClose;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("reviewComplaints.sheet.title");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <SheetTitle>{t5}</SheetTitle>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== complaint || $[11] !== t) {
    t7 = complaint ? `${complaint.shop.name} · ${formatDateTime(complaint.createdAt)}` : t("reviewComplaints.loading");
    $[10] = complaint;
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <SheetDescription>{t7}</SheetDescription>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t6 || $[16] !== t8) {
    t9 = <SheetHeader>{t6}{t8}</SheetHeader>;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== complaint || $[19] !== error) {
    t10 = error && !complaint && <p className="px-4 text-sm text-destructive">{(error as Error).message}</p>;
    $[18] = complaint;
    $[19] = error;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== canAudit || $[22] !== canModerate || $[23] !== complaint || $[24] !== t) {
    t11 = complaint && <div className="flex flex-col gap-5 px-4 pb-6"><section className="flex flex-col gap-2"><h3 className="text-sm font-semibold">{t("reviewComplaints.sheet.product")}</h3><Link to={`/products/${encodeURIComponent(complaint.product.id)}`} className="rounded-md border p-2 text-sm hover:bg-muted"><ProductCell product={complaint.product} /></Link></section><section className="flex flex-col gap-2 rounded-lg border p-3" data-testid="complaint-review"><div className="flex flex-wrap items-center justify-between gap-2"><h3 className="text-sm font-semibold">{t("reviewComplaints.sheet.review")}</h3>{complaint.review.hidden ? <Badge variant="destructive">{t("reviewComplaints.sheet.hidden")}</Badge> : <Badge variant="outline">{t("reviewComplaints.sheet.visible")}</Badge>}</div><div className="flex flex-wrap items-center gap-2 text-sm"><span className="font-medium">{complaint.review.authorName}</span>{complaint.review.anonymous && <Badge variant="secondary">{t("reviewComplaints.sheet.anonymous")}</Badge>}<span className="text-xs text-muted-foreground">{formatDateTime(complaint.review.createdAt)}</span></div><Stars value={complaint.review.rating} /><p className="whitespace-pre-line break-words text-sm">{complaint.review.text || t("reviewComplaints.noText")}</p>{complaint.review.photos.length > 0 && <div className="flex flex-wrap gap-2">{complaint.review.photos.map(_temp4)}</div>}{complaint.review.sellerReply && <div className="rounded-md bg-muted/60 px-3 py-2 text-sm"><span className="text-muted-foreground">{t("reviewComplaints.sheet.reply")}: </span>{complaint.review.sellerReply}</div>}{complaint.review.hidden && complaint.review.hiddenReason && <p className="text-sm text-destructive">{t("reviewComplaints.sheet.hiddenReason", {
            reason: complaint.review.hiddenReason,
            date: complaint.review.hiddenAt ? formatDateTime(complaint.review.hiddenAt) : ""
          })}</p>}</section><section className="flex flex-col gap-2 rounded-lg border p-3" data-testid="complaint-details"><div className="flex flex-wrap items-center justify-between gap-2"><h3 className="text-sm font-semibold">{t("reviewComplaints.sheet.complaint")}</h3><span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_CLASS[complaint.status])}>{t(`reviewComplaints.status.${complaint.status}`)}</span></div><dl className="grid gap-2 text-sm sm:grid-cols-2"><div><dt className="text-muted-foreground">{t("reviewComplaints.sheet.reason")}</dt><dd className="font-medium">{complaint.reason}</dd></div><div><dt className="text-muted-foreground">{t("reviewComplaints.sheet.author")}</dt><dd>{complaint.shop.name}{complaint.authorName ? ` · ${complaint.authorName}` : ""}</dd></div></dl>{complaint.description && <p className="whitespace-pre-line break-words rounded-md bg-muted/60 px-3 py-2 text-sm">{complaint.description}</p>}{complaint.attachments.length > 0 && <div className="flex flex-wrap gap-2">{complaint.attachments.map((src_0, index) => <a key={src_0} href={src_0} target="_blank" rel="noreferrer" className="text-sm text-primary underline-offset-4 hover:underline">{t("reviewComplaints.sheet.attachment", {
              index: index + 1
            })}</a>)}</div>}{complaint.otherComplaints > 0 && <p className="text-xs text-muted-foreground">{t("reviewComplaints.sheet.otherComplaints", {
            count: complaint.otherComplaints
          })}</p>}{complaint.status !== "open" && <div className="flex flex-col gap-1 border-t pt-2 text-sm" data-testid="complaint-decision"><span><span className="text-muted-foreground">{t("reviewComplaints.sheet.decision")}: </span>{t(`reviewComplaints.decision.${complaint.status}`)}</span>{complaint.decisionReason && <span><span className="text-muted-foreground">{t("reviewComplaints.sheet.decisionReason")}: </span>{complaint.decisionReason}</span>}<span className="text-xs text-muted-foreground">{[complaint.decidedBy?.name, complaint.resolvedAt ? formatDateTime(complaint.resolvedAt) : ""].filter(Boolean).join(" \xB7 ")}</span></div>}</section>{canModerate && <div className="flex flex-wrap gap-2">{complaint.status === "open" && <><Button variant="destructive" onClick={() => setPending({
            kind: "hide"
          })} data-testid="complaint-hide"><EyeOff className="mr-2 size-4" />{t("reviewComplaints.actions.hide")}</Button><Button variant="outline" onClick={() => setPending({
            kind: "keep"
          })} data-testid="complaint-keep"><Check className="mr-2 size-4" />{t("reviewComplaints.actions.keep")}</Button></>}{complaint.review.hidden && <Button variant="outline" onClick={() => setPending({
          kind: "restore"
        })} data-testid="complaint-restore"><RotateCcw className="mr-2 size-4" />{t("reviewComplaints.actions.restore")}</Button>}</div>}{canAudit && <Link to={`/audit?q=${encodeURIComponent(complaint.review.id)}`} className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="complaint-audit-link"><ScrollText className="size-4" />{t("reviewComplaints.sheet.audit")}</Link>}</div>;
    $[21] = canAudit;
    $[22] = canModerate;
    $[23] = complaint;
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== complaint || $[27] !== pending) {
    t12 = complaint && <DecisionDialog complaint={complaint} pending={pending} onClose={() => setPending(null)} />;
    $[26] = complaint;
    $[27] = pending;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12 || $[32] !== t9) {
    t13 = <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="complaint-sheet">{t9}{t10}{t11}{t12}</SheetContent>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t9;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t13 || $[35] !== t3 || $[36] !== t4) {
    t14 = <Sheet open={t3} onOpenChange={t4}>{t13}</Sheet>;
    $[34] = t13;
    $[35] = t3;
    $[36] = t4;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  return t14;
}
function _temp4(src) {
  return <a key={src} href={src} target="_blank" rel="noreferrer" className="block overflow-hidden rounded"><img src={src} alt="" className="h-24 w-[72px] object-cover" /></a>;
}
function _temp3(s) {
  return s.user;
}
function DecisionDialog(t0) {
  const $ = _c(64);
  const {
    complaint,
    pending,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    decide,
    restore
  } = useReviewModeration();
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const busy = decide.isPending || restore.isPending;
  const kind = pending?.kind;
  let t1;
  if ($[0] !== onClose) {
    t1 = () => {
      setReason("");
      setError(null);
      onClose();
    };
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const close = t1;
  let t2;
  if ($[2] !== close || $[3] !== complaint || $[4] !== decide || $[5] !== kind || $[6] !== reason || $[7] !== restore || $[8] !== t) {
    t2 = async () => {
      if (!kind) {
        return;
      }
      if (!reason.trim()) {
        return setError(t("reviewComplaints.dialog.reasonRequired"));
      }
      setError(null);
      ;
      try {
        if (kind === "restore") {
          await restore.mutateAsync({
            reviewId: complaint.review.id,
            reason: reason.trim()
          });
        } else {
          await decide.mutateAsync({
            id: complaint.id,
            action: kind,
            reason: reason.trim()
          });
        }
        close();
      } catch (t3) {
        const e = t3;
        setError((e as Error).message);
      }
    };
    $[2] = close;
    $[3] = complaint;
    $[4] = decide;
    $[5] = kind;
    $[6] = reason;
    $[7] = restore;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const submit = t2;
  const t3 = Boolean(kind);
  let t4;
  if ($[10] !== close) {
    t4 = open => !open && close();
    $[10] = close;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== kind || $[13] !== t) {
    t5 = kind ? t(`reviewComplaints.dialog.${kind}.title`) : "";
    $[12] = kind;
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <DialogTitle>{t5}</DialogTitle>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== kind || $[18] !== t) {
    t7 = kind ? t(`reviewComplaints.dialog.${kind}.description`) : "";
    $[17] = kind;
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = <DialogDescription>{t7}</DialogDescription>;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t8) {
    t9 = <DialogHeader>{t6}{t8}</DialogHeader>;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t) {
    t10 = t("reviewComplaints.dialog.placeholder");
    $[25] = t;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t) {
    t11 = t("reviewComplaints.dialog.reason");
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = e_0 => setReason(e_0.target.value);
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== reason || $[31] !== t10 || $[32] !== t11) {
    t13 = <textarea className="min-h-[96px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={reason} maxLength={500} placeholder={t10} aria-label={t11} onChange={t12} data-testid="decision-reason" />;
    $[30] = reason;
    $[31] = t10;
    $[32] = t11;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== error) {
    t14 = error && <p className="text-sm text-destructive">{error}</p>;
    $[34] = error;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("reviewComplaints.dialog.cancel");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== busy || $[39] !== close || $[40] !== t15) {
    t16 = <Button variant="outline" onClick={close} disabled={busy}>{t15}</Button>;
    $[38] = busy;
    $[39] = close;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const t17 = kind === "hide" ? "destructive" : "default";
  let t18;
  if ($[42] !== submit) {
    t18 = () => void submit();
    $[42] = submit;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== kind || $[45] !== t) {
    t19 = kind ? t(`reviewComplaints.actions.${kind}`) : "";
    $[44] = kind;
    $[45] = t;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== busy || $[48] !== t17 || $[49] !== t18 || $[50] !== t19) {
    t20 = <Button variant={t17} onClick={t18} disabled={busy} data-testid="decision-submit">{t19}</Button>;
    $[47] = busy;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t16 || $[53] !== t20) {
    t21 = <DialogFooter>{t16}{t20}</DialogFooter>;
    $[52] = t16;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t13 || $[56] !== t14 || $[57] !== t21 || $[58] !== t9) {
    t22 = <DialogContent className="sm:max-w-lg" data-testid="decision-dialog">{t9}{t13}{t14}{t21}</DialogContent>;
    $[55] = t13;
    $[56] = t14;
    $[57] = t21;
    $[58] = t9;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t22 || $[61] !== t3 || $[62] !== t4) {
    t23 = <Dialog open={t3} onOpenChange={t4}>{t22}</Dialog>;
    $[60] = t22;
    $[61] = t3;
    $[62] = t4;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  return t23;
}